import { FEATURE_FLAGS } from '@constants';
import { defineStore } from 'pinia';

import { useLaunchDarkly } from '~/composables/useLaunchDarkly';

import { useApplicationStore } from './application';
import { Season, useSeasonalPreferencesStore } from './seasonal-preferences';

export const HALLOWEEN_EGGS = [
  {
    description: 'The sidebar logo becomes a harvest moon with the OP shield on it, and a bat crosses it now and then.',
    key: 'moonLogo',
    label: 'Moon logo',
  },
  {
    description: 'Ghosts, pumpkins, cauldrons and friends stand in for the sidebar icons.',
    key: 'icons',
    label: 'Spooky icons',
  },
  { description: 'A small colony crosses the screen every so often.', key: 'bats', label: 'Bats' },
  {
    description: 'Rests on the web and walks a lap of it every so often; a click sends it scurrying off.',
    key: 'spider',
    label: 'Spider',
  },
  { description: 'An orb web in the top-right corner, swaying in a draught.', key: 'cobwebs', label: 'Cobweb' },
  {
    description: 'Every border and divider takes on a pumpkin-orange tint.',
    key: 'orangeBorders',
    label: 'Orange borders',
  },
  {
    description: 'Page titles and headings switch to a creepy display face.',
    key: 'spookyFont',
    label: 'Spooky headings',
  },
  {
    description: 'A darker night sky behind the dashboard, with lightning now and then.',
    key: 'storm',
    label: 'Storm',
  },
  {
    description: 'Once in a while a click dims the lights and the dark fills with watching eyes.',
    key: 'eyes',
    label: 'Eyes in the dark',
  },
  { description: 'Type "boo" anywhere outside a text field for a surprise.', key: 'boo', label: 'Boo' },
] as const;

export type HalloweenEgg = (typeof HALLOWEEN_EGGS)[number]['key'];

// Device-only state; the person's own on/off and egg choices live server-side in the seasonal preferences store.
export type SeasonalPrefs = {
  // Set once the device has been switched to dark; survives a flag-off restore so a transient "off" read (LaunchDarkly
  // failing to load once) can never re-force dark on someone who went back to Light.
  darkForced?: boolean;
  // What the device showed before the flag switched it to dark, and what its `user.theme` cookie held (null when the
  // theme came from the system preference); both are put back when the flag goes off.
  previousTheme?: string;
  previousThemeCookie?: string | null;
};

export const SEASONAL_THEME_COOKIE = 'user.seasonalTheme';
export const HALLOWEEN_ROOT_CLASS = 'theme-halloween';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

// Prefixed so the <html> markers never collide with the decor element classes (.hw-spider, .hw-bat, ...).
const EGG_ROOT_CLASS: Record<HalloweenEgg, string> = {
  bats: 'hw-egg-bats',
  boo: 'hw-egg-boo',
  cobwebs: 'hw-egg-cobwebs',
  eyes: 'hw-egg-eyes',
  icons: 'hw-egg-icons',
  moonLogo: 'hw-egg-moon-logo',
  orangeBorders: 'hw-egg-orange-borders',
  spider: 'hw-egg-spider',
  spookyFont: 'hw-egg-spooky-font',
  storm: 'hw-egg-storm',
};

const defaultPrefs = (): SeasonalPrefs => ({});

/**
 * Halloween theme state. It shows only while the seasonal themes flag is on, engineering has switched Halloween on for
 * everyone, the person has not switched it off for themselves, and the device is in dark mode. While it is live each
 * device is switched to dark mode once (and switched back when it stops); picking Light again afterwards is respected.
 */
export const useSeasonalThemeStore = defineStore('seasonalTheme', () => {
  const prefs = useCookie<SeasonalPrefs>(SEASONAL_THEME_COOKIE, {
    default: defaultPrefs,
    maxAge: COOKIE_MAX_AGE_SECONDS,
  });
  const application = useApplicationStore();
  const seasonal = useSeasonalPreferencesStore();
  const userTheme = useCookie<string | null>('user.theme');
  const platformEnabled = ref(false);
  let synced = false;

  const darkMode = computed(() => application.theme === 'dark');

  // Kept as `showOnDevice` for the Themes page; it is the person's choice and follows them to every device.
  const showOnDevice = computed(() => seasonal.personalEnabled(Season.HALLOWEEN));
  const globallyEnabled = computed(() => seasonal.globalEnabled(Season.HALLOWEEN));
  const live = computed(() => platformEnabled.value && globallyEnabled.value && showOnDevice.value);
  const active = computed(() => live.value && darkMode.value);

  function isEggEnabled(key: HalloweenEgg): boolean {
    return active.value && seasonal.eggEnabled(Season.HALLOWEEN, key);
  }

  const htmlClasses = computed(() => {
    if (!active.value) return [];

    return [
      HALLOWEEN_ROOT_CLASS,
      ...HALLOWEEN_EGGS.filter(egg => isEggEnabled(egg.key)).map(egg => EGG_ROOT_CLASS[egg.key]),
    ];
  });

  // Opting back in is an explicit ask for the theme, so the device may be switched to dark again.
  function setShowOnDevice(value: boolean) {
    if (value) prefs.value = { ...(prefs.value ?? defaultPrefs()), darkForced: false };
    seasonal.setPersonal(Season.HALLOWEEN, value);
  }

  // Dark mode is forced once per device while the flag is on; a user who then picks Light stays on Light.
  function switchToDark() {
    const current = prefs.value ?? defaultPrefs();

    if (!live.value || current.darkForced) return;

    prefs.value = {
      ...current,
      darkForced: true,
      // The saved cookie is the user's real preference; the page theme can be transient (/analytics forces light).
      previousTheme: userTheme.value || application.theme || 'light',
      previousThemeCookie: userTheme.value ?? null,
    };
    application.setTheme('dark');
    userTheme.value = 'dark';
  }

  function restoreTheme() {
    const current = prefs.value ?? defaultPrefs();

    if (current.previousTheme === undefined) return;

    const { previousTheme, previousThemeCookie, ...rest } = current;

    prefs.value = rest;

    // A device that moved off dark after the switch made its own choice; leave that alone.
    if (userTheme.value !== 'dark') return;

    application.setTheme(previousTheme);
    userTheme.value = previousThemeCookie ?? null;
  }

  // Dark mode follows the theme going live or stopping, but only once the person's saved choice has loaded, so a new
  // device is never switched to dark for someone who turned the theme off for themselves.
  watch(
    () => [live.value, seasonal.loaded] as const,
    ([isLive, loaded]) => {
      if (!loaded) return;
      if (isLive) switchToDark();
      else restoreTheme();
    },
    { flush: 'sync' },
  );

  function setEgg(key: HalloweenEgg, enabled: boolean) {
    seasonal.setEgg(Season.HALLOWEEN, key, enabled);
  }

  // Reads the platform default once and follows later flag changes; safe to call from several components.
  function sync() {
    if (synced) return;
    synced = true;

    const { getFlag, onFlagChange } = useLaunchDarkly();

    platformEnabled.value = !!getFlag(FEATURE_FLAGS.SEASONAL_THEMES, false);
    onFlagChange(FEATURE_FLAGS.SEASONAL_THEMES, value => {
      platformEnabled.value = !!value;
    });
    seasonal.load();
  }

  return {
    active,
    darkMode,
    globallyEnabled,
    htmlClasses,
    isEggEnabled,
    platformEnabled,
    prefs,
    setEgg,
    setShowOnDevice,
    showOnDevice,
    sync,
  };
});
